/* UFORIQ cart drawer (2026-10-13). In-house replacement for the Ego (formerly Opus) Cart Upsell
 * drawer. Markup: sections/uq-cart-drawer.liquid (+ sections/uq-cart-products.liquid for cards).
 * Pure logic: assets/uq-cart-logic.js. Early click/submit capture: snippets/uq-cart-head.liquid.
 * Per-location stock limits: assets/uq-location-stock.js (window.UqStock); every add, quantity change
 * and checkout here is capped at what the fulfilling location has (see that file).
 *
 * Cart AJAX API for every change, with bundled Section Rendering (`sections=uq-cart-drawer`) so one
 * request returns the fresh drawer parts. All cart writes go through one queue so fast clicks
 * can't race each other.
 *
 * Small order fee (2026-10-21, replaced the $20 order minimum): the scheduling widget adds and removes
 * one "Small Order Fee" line (data.lines[].fee). Here it is shown as a fee row with a hint, never as an
 * item: no stepper or remove button, no upsells, no stock limits, not in the free-delivery total.
 *
 * Coexistence with Ego/Opus: while the app embed is still enabled, snippets/uq-cart-head.liquid
 * registers window-level capture listeners before any app script runs. When this drawer handles a
 * header-cart click or an add-to-cart submit it calls stopImmediatePropagation(), so the app never
 * sees it, and the app's own containers are hidden by CSS. See docs/CART.md.
 */
(function () {
  'use strict';

  var L = window.UqCartLogic;
  if (!L || !document.querySelector('[data-uq-cart]')) return;

  var SECTION = 'uq-cart-drawer';
  var CARDS_SECTION = 'uq-cart-products';
  var UNDO_MS = 5000;
  var STALE_FLAG = 'uqc_stale_cleared';
  var html = document.documentElement;
  var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var nativeFetch = window.fetch.bind(window);

  var root, panel, data = {};
  var isOpen = false;
  var opener = null;
  var inerted = [];
  var queue = Promise.resolve();
  var addonPromise = null;
  var addonInfo = null;
  var cardCache = {};
  var upsellSig = null;
  var bestLoaded = false;
  var lastReached = null;
  var undoState = null;
  var lastRefresh = Date.now();
  var pendingQty = {};
  var closeTimer = null;
  var staleNote = false;
  var lastOverSig = '';

  /* ---------------- helpers ---------------- */

  function $(sel, ctx) { return (ctx || root).querySelector(sel); }
  function $$(sel, ctx) { return Array.prototype.slice.call((ctx || root).querySelectorAll(sel)); }
  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
  function money(cents) { return L.formatMoney(cents, data.moneyFormat); }
  function cartUrl(path) { return (root.dataset.cartUrl || '/cart') + path; }
  function rootUrl() { return (root.dataset.rootUrl || '/').replace(/\/?$/, '/'); }
  function areaLabel(area) {
    var labels = (window.UforiqLocation && window.UforiqLocation.LABELS) || {};
    return labels[area] || { austin: 'Austin Metro', lubbock: 'Lubbock Metro', nationwide: 'Nationwide' }[area] || '';
  }
  function getArea() {
    var a = null;
    try {
      if (window.UforiqLocation && typeof window.UforiqLocation.get === 'function') a = window.UforiqLocation.get();
      else if (window.UforiqArea) a = window.UforiqArea.get(); // area in effect (2026-11-02, visit areas)
      else {
        var raw = localStorage.getItem('uforiq.location');
        a = raw ? JSON.parse(raw).location : null;
      }
    } catch (e) { a = null; }
    return L.normalizeArea(a);
  }
  /* Metro of a scheduling-widget zone id (Delivery-Location-Id), from the widget's config JSON. */
  var zoneCache = { text: null, map: {}, feeThreshold: null };
  function widgetConfig() {
    var el = document.querySelector('[data-uqs-config]');
    var text = el ? el.textContent : '';
    if (text !== zoneCache.text) {
      var map = {};
      var feeThreshold = null;
      try {
        var cfg = JSON.parse(text);
        (cfg.zones || []).forEach(function (z) { if (z && z.id != null) map[String(z.id)] = z.metro; });
        if (cfg.smallOrderFee && Number(cfg.smallOrderFee.threshold) > 0) feeThreshold = Math.round(Number(cfg.smallOrderFee.threshold) * 100);
      } catch (e) { /* no widget config on this page */ }
      zoneCache = { text: text, map: map, feeThreshold: feeThreshold };
    }
    return zoneCache;
  }
  function zoneMetro(id) {
    if (id == null || id === '') return null;
    return widgetConfig().map[String(id)] || null;
  }
  /* The cart's checkout method and zone: the widget's live state, then its last change event,
   * then the saved cart attributes. */
  var lastSched = null;
  function cartMethod() {
    var st = null;
    try {
      if (window.UforiqSchedule && typeof window.UforiqSchedule.getState === 'function') st = window.UforiqSchedule.getState();
    } catch (e) { st = null; }
    if (!(st && st.method)) st = lastSched;
    if (st && st.method) return { method: st.method, zoneId: st.zoneId };
    var a = data.attributes || {};
    return { method: a['Checkout-Method'] || null, zoneId: a['Delivery-Location-Id'] || null };
  }
  function session(key, value) {
    try {
      if (value === undefined) return sessionStorage.getItem(key);
      if (value === null) sessionStorage.removeItem(key);
      else sessionStorage.setItem(key, value);
    } catch (e) { /* private mode */ }
    return null;
  }

  function announce(msg) {
    var live = $('[data-uqc-live]');
    if (!live) return;
    live.textContent = '';
    setTimeout(function () { live.textContent = msg; }, 60);
  }
  function alertMsg(msg) {
    var box = $('[data-uqc-alert]');
    if (!box) return;
    box.textContent = '';
    setTimeout(function () { box.textContent = msg; }, 60);
  }

  function enqueue(task) {
    var p = queue.then(task);
    queue = p.catch(function () {});
    return p;
  }

  function parseJson(res) {
    return res.json().catch(function () { return {}; }).then(function (json) {
      if (!res.ok) {
        var err = new Error(json.description || json.message || 'Cart request failed');
        err.description = json.description || json.message || '';
        err.status = res.status;
        throw err;
      }
      return json;
    });
  }

  function post(path, body) {
    body = body || {};
    body.sections = SECTION;
    body.sections_url = window.location.pathname;
    return nativeFetch(cartUrl(path), {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify(body)
    }).then(parseJson);
  }

  function friendlyError(err) {
    if (err && err.description) return err.description;
    return "We couldn't update your cart. Check your connection and try again.";
  }

  /* ---------------- data + sections ---------------- */

  function readData() {
    var el = $('[data-uqc-part="data"]');
    try { data = JSON.parse(el ? el.textContent : '{}') || {}; } catch (e) { data = {}; }
    data.lines = data.lines || [];
    data.attributes = data.attributes || {};
    data.discountCodes = data.discountCodes || [];
    // The fee line is untracked and never limited: the stock module treats it like an add-on.
    if (window.UqStock) window.UqStock.setCart(data.lines.map(function (l) {
      return l.fee ? Object.assign({}, l, { addon: true, limited: false }) : l;
    }), data.attributes);
  }

  function realLines() { return data.lines.filter(function (l) { return !l.addon && !l.fee; }); }
  function addonLines() { return data.lines.filter(function (l) { return l.addon; }); }
  /* Priority Delivery and the small order fee: lines that only make sense next to real items. */
  function extraLines() { return data.lines.filter(function (l) { return l.addon || l.fee; }); }
  function addonHandles() { return [root.dataset.addonHandle, root.dataset.feeHandle].filter(Boolean); }
  function lineByKey(key) {
    for (var i = 0; i < data.lines.length; i++) if (data.lines[i].key === key) return data.lines[i];
    return null;
  }

  function focusDescriptor() {
    var a = document.activeElement;
    if (!a || !root.contains(a)) return null;
    var line = a.closest('[data-uqc-line]');
    if (!line) return null;
    var kind = a.matches('[data-uqc-step="1"]') ? 'inc'
      : a.matches('[data-uqc-step="-1"]') ? 'dec'
      : a.matches('[data-uqc-qty-input]') ? 'input'
      : a.matches('[data-uqc-remove]') ? 'remove' : 'title';
    return { key: line.getAttribute('data-key'), kind: kind, index: $$('[data-uqc-line]').indexOf(line) };
  }

  function restoreFocus(d) {
    var sel = { inc: '[data-uqc-step="1"]', dec: '[data-uqc-step="-1"]', input: '[data-uqc-qty-input]', remove: '[data-uqc-remove]', title: '.uqc-line__title' }[d.kind];
    var lines = $$('[data-uqc-line]');
    var line = lines.filter(function (l) { return l.getAttribute('data-key') === d.key; })[0];
    var el = line && line.querySelector(sel);
    if (!el && lines.length) el = lines[Math.min(d.index, lines.length - 1)].querySelector('.uqc-line__title');
    (el || panel).focus();
  }

  function applyHtml(markup) {
    var doc = new DOMParser().parseFromString(markup, 'text/html');
    var fd = focusDescriptor();
    $$('[data-uqc-part]', doc).forEach(function (fresh) {
      var cur = $('[data-uqc-part="' + fresh.getAttribute('data-uqc-part') + '"]');
      if (cur) cur.innerHTML = fresh.innerHTML;
    });
    lastRefresh = Date.now();
    readData();
    render();
    // The scheduling widget sits outside the patched parts: let it re-read the cart (subtotal, Priority line).
    if (window.UforiqSchedule && typeof window.UforiqSchedule.refresh === 'function') {
      window.UforiqSchedule.refresh().catch(function () {});
    }
    if (fd && isOpen && !root.contains(document.activeElement)) restoreFocus(fd);
  }

  function applySections(sections) {
    var markup = sections && sections[SECTION];
    if (markup) { applyHtml(markup); return Promise.resolve(); }
    return refresh();
  }

  function refresh() {
    return nativeFetch(rootUrl() + '?sections=' + SECTION, { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then(parseJson)
      .then(function (json) { if (json && json[SECTION]) applyHtml(json[SECTION]); })
      .catch(function () { /* keep what we have */ });
  }

  /* ---------------- rendering ---------------- */

  function syncHeaderPill() {
    var iso = (window.KROWN && window.KROWN.settings && window.KROWN.settings.iso_code) || '';
    document.querySelectorAll('[data-header-cart-count]').forEach(function (el) {
      if (!root.contains(el)) el.textContent = data.itemCount || 0;
    });
    document.querySelectorAll('[data-header-cart-total]').forEach(function (el) {
      el.textContent = money(data.totalPrice || 0) + iso;
    });
  }

  function render() {
    var empty = !(data.realCount > 0);
    $('[data-uqc-empty]').hidden = !empty;
    $('[data-uqc-footer]').hidden = empty;
    var sched = $('[data-uqc-sched]');
    if (sched) sched.hidden = empty;
    $('[data-uqc-note]').hidden = empty || !(staleNote || session(STALE_FLAG) === '1');
    syncHeaderPill();
    syncCards();
    renderRewards();
    renderFee();
    renderAddon();
    renderStock();
    if (empty) {
      $('[data-uqc-upsells]').hidden = true;
      if (isOpen) loadBest();
    } else if (isOpen) {
      loadUpsells();
    }
  }

  var ICON_PIN = '<svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path fill="currentColor" d="M12 2a7 7 0 0 0-7 7c0 5.25 7 13 7 13s7-7.75 7-13a7 7 0 0 0-7-7Zm0 9.5A2.5 2.5 0 1 1 12 6.5a2.5 2.5 0 0 1 0 5Z"/></svg>';
  var ICON_BOX = '<svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path fill="currentColor" d="M3 7.5 12 3l9 4.5v9L12 21l-9-4.5v-9Zm9 1.7 6.4-3.2L12 2.9 5.6 6 12 9.2Zm-7 6.1 6 3v-7.1l-6-3v7.1Zm8 3 6-3V8.2l-6 3v7.1Z"/></svg>';
  var ICON_CHECK = '<svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path fill="currentColor" d="M9.6 16.2 5.4 12l-1.4 1.4 5.6 5.6L20 8.6l-1.4-1.4z"/></svg>';

  function renderRewards() {
    var box = $('[data-uqc-rewards]');
    if (!(data.realCount > 0)) { box.hidden = true; return; }
    box.hidden = false;
    var m = cartMethod();
    var banner = L.bannerArea(getArea(), m.method, zoneMetro(m.zoneId));
    var area = banner.area;
    if (!area) {
      box.className = 'uqc-rewards uqc-rewards--prompt';
      box.innerHTML =
        '<p class="uqc-rewards__msg">Choose your delivery area to see when delivery or shipping is free.</p>' +
        '<button type="button" class="uqc-btn uqc-btn--outline uqc-btn--sm" data-uqc-choose-area>' + ICON_PIN + '<span>Choose delivery area</span></button>';
      lastReached = null;
      return;
    }
    var p = L.rewardProgress(data.rewardsTotal || 0, area);
    var label = areaLabel(area);
    box.className = 'uqc-rewards' + (p.reached ? ' is-reached' : '');
    box.innerHTML =
      '<div class="uqc-rewards__head">' +
        '<span class="uqc-rewards__area">' + (area === 'nationwide' ? ICON_BOX : ICON_PIN) + '<span>' + (banner.shipping ? 'Shipping ' : 'Delivering to ') + '<strong>' + esc(label) + '</strong></span></span>' +
        '<button type="button" class="uqc-link" data-uqc-choose-area aria-label="Change delivery area, currently ' + esc(label) + '">Change</button>' +
      '</div>' +
      '<p class="uqc-rewards__msg">' + (p.reached ? ICON_CHECK : '') + '<span>' + esc(L.rewardMessage(p, data.moneyFormat)) + '</span></p>' +
      '<div class="uqc-progress" role="progressbar" aria-label="' + esc(p.aria) + '" aria-valuemin="0" aria-valuemax="100" aria-valuenow="' + p.percent + '" aria-valuetext="' + esc(money(p.total) + ' of ' + money(p.threshold)) + '">' +
        '<span class="uqc-progress__bar" style="width:' + p.percent + '%"></span>' +
      '</div>' +
      '<p class="uqc-rewards__tier">' + esc(p.label) + '</p>';
    var key = area + ':' + p.reached;
    if (lastReached !== null && lastReached !== key && p.reached && isOpen) announce(L.rewardMessage(p, data.moneyFormat) + '.');
    lastReached = key;
  }

  /* ---------------- small order fee ---------------- */

  /* The fee row is server-rendered (data-uqc-part="fee") while the cart holds the fee line; this
   * fills its hint: "Add $X more to remove the small order fee". */
  function renderFee() {
    var hint = $('[data-uqc-fee-hint]');
    if (!hint) return;
    var h = L.smallOrderFeeHint(data.rewardsTotal || 0, data.moneyFormat, widgetConfig().feeThreshold);
    hint.textContent = h ? h.message : '';
    hint.hidden = !h;
  }

  /* ---------------- per-location stock limits ---------------- */

  var STOCK_BLOCK_MSG = "Some items in your cart are over what's available. Lower the quantities above to check out.";

  function stockVerdicts() {
    return window.UqStock ? window.UqStock.evaluate(realLines().map(function (l) {
      return { key: l.key, variantId: l.variantId, qty: l.qty, addon: false };
    })) : [];
  }
  function verdictFor(key) {
    var v = stockVerdicts().filter(function (x) { return x.key === key; })[0];
    return v && v.limit !== null ? v : null;
  }
  function stockBlocked() { return stockVerdicts().some(function (v) { return v.over; }); }

  /* -> { allowed, capped, remaining, limit, message } or null when the stock module isn't loaded. */
  function stockCheck(variantId, qty) {
    var St = window.UqStock;
    if (!St || !variantId) return null;
    St.scan(root);
    return St.checkAdd(variantId, qty);
  }
  function freshStock(ids) {
    var St = window.UqStock;
    if (!St) return Promise.resolve(false);
    St.scan(root);
    return St.refresh(ids);
  }

  function renderStock() {
    if (!root) return;
    var verdicts = stockVerdicts();
    var byKey = {};
    verdicts.forEach(function (v) { byKey[v.key] = v; });
    var overSig = [];
    $$('[data-uqc-line]').forEach(function (li) {
      var key = li.getAttribute('data-key');
      var v = byKey[key];
      var limited = !!(v && v.limit !== null);
      var inc = $('[data-uqc-step="1"]', li);
      if (inc) {
        var atMax = limited && v.qty >= v.maxForLine;
        inc.classList.toggle('is-disabled', atMax);
        if (atMax) inc.setAttribute('aria-disabled', 'true'); else inc.removeAttribute('aria-disabled');
      }
      var input = $('[data-uqc-qty-input]', li);
      if (input) { if (limited) input.setAttribute('max', String(Math.max(v.maxForLine, v.qty))); else input.removeAttribute('max'); }
      var box = $('[data-uqc-stock]', li);
      if (!box) return;
      if (limited && v.over) {
        $('[data-uqc-stock-msg]', box).textContent = v.message;
        var btn = $('[data-uqc-reduce]', box);
        btn.setAttribute('data-uqc-reduce', String(v.reduceTo));
        var line = lineByKey(key);
        btn.textContent = v.reduceTo > 0 ? 'Reduce to ' + v.reduceTo : 'Remove item';
        btn.setAttribute('aria-label', (v.reduceTo > 0 ? 'Reduce ' + (line ? line.title : 'item') + ' to ' + v.reduceTo : 'Remove ' + (line ? line.title : 'item')));
        box.hidden = false;
        overSig.push(key + ':' + v.limit + ':' + v.message);
      } else {
        box.hidden = true;
      }
    });
    var over = overSig.length > 0;
    var block = $('[data-uqc-stock-block]');
    if (block) block.hidden = !over;
    var co = $('[data-uqc-checkout]');
    if (co) {
      co.classList.toggle('is-disabled', over);
      if (over) co.setAttribute('aria-disabled', 'true'); else co.removeAttribute('aria-disabled');
    }
    var sigStr = overSig.join('|');
    if (sigStr !== lastOverSig && over && isOpen) {
      var first = verdicts.filter(function (v) { return v.over; })[0];
      var fl = first && lineByKey(first.key);
      announce((fl ? fl.title + '. ' : '') + first.message + ' ' + STOCK_BLOCK_MSG);
    }
    lastOverSig = sigStr;
  }

  function stockAlertAndFocus() {
    renderStock();
    alertMsg(STOCK_BLOCK_MSG);
    var b = root.querySelector('[data-uqc-stock]:not([hidden]) [data-uqc-reduce]');
    if (b && isOpen) b.focus();
  }

  function loadAddon() {
    var handle = root.dataset.addonHandle;
    if (!handle) return Promise.resolve(null);
    if (!addonPromise) {
      addonPromise = nativeFetch(rootUrl() + 'products/' + encodeURIComponent(handle) + '.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (p) {
          if (!p || !p.available || !p.variants) return null;
          var v = p.variants.filter(function (x) { return x.available; })[0];
          return v ? { variantId: v.id, price: v.price } : null;
        })
        .catch(function () { return null; })
        .then(function (info) { addonInfo = info; renderAddon(); return info; });
    }
    return addonPromise;
  }

  // Priority Delivery is a local same-day delivery add-on: Austin and Lubbock only, never Nationwide shipping.
  function addonAllowed() {
    var a = getArea();
    return a === 'austin' || a === 'lubbock';
  }

  function renderAddon() {
    var row = $('[data-uqc-addon]');
    if (!row) return;
    var on = data.addonQty > 0;
    // Hidden until the add-on product loads, and outside the local delivery areas; also hidden when the
    // product is missing or unavailable, unless it is already in the cart (so it can still be switched off).
    row.hidden = !(data.realCount > 0) || (!on && (!addonInfo || !addonAllowed()));
    var toggle = $('[data-uqc-addon-toggle]');
    toggle.setAttribute('aria-checked', on ? 'true' : 'false');
    var price = $('[data-uqc-addon-price]');
    if (addonInfo) price.textContent = money(addonInfo.price);
  }

  /* ---------------- cards (upsells, best sellers) ---------------- */

  function fetchCards(url) {
    if (!cardCache[url]) {
      cardCache[url] = nativeFetch(url, { credentials: 'same-origin' })
        .then(function (r) { return r.ok ? r.text() : ''; })
        .then(function (text) {
          var doc = new DOMParser().parseFromString(text, 'text/html');
          return $$('[data-uqc-card]', doc).map(function (li) {
            var d = li.dataset;
            return {
              id: d.productId, handle: d.handle, title: d.title, url: d.url, type: d.type,
              tags: d.tags, available: d.available, giftCard: d.giftCard, html: li.outerHTML
            };
          });
        })
        .catch(function () { delete cardCache[url]; return []; });
    }
    return cardCache[url];
  }

  function recUrl(productId, intent) {
    var base = root.dataset.recsUrl || '/recommendations/products';
    return base + '?product_id=' + encodeURIComponent(productId) + '&limit=10&intent=' + intent + '&section_id=' + CARDS_SECTION;
  }

  function cartProductIds() { return realLines().map(function (l) { return String(l.productId); }); }

  function loadUpsells() {
    var wrap = $('[data-uqc-upsells]');
    if (root.dataset.upsells === 'false' || !(data.realCount > 0)) { wrap.hidden = true; return; }
    var ids = cartProductIds();
    var seeds = L.recommendationSeeds(ids, 4);
    var sig = seeds.join(',') + '|' + ids.slice().sort().join(',');
    if (sig === upsellSig) return;
    upsellSig = sig;
    var reqs = seeds.map(function (id) { return fetchCards(recUrl(id, 'complementary')); })
      .concat(seeds.map(function (id) { return fetchCards(recUrl(id, 'related')); }));
    Promise.all(reqs).then(function (lists) {
      if (sig !== upsellSig) return;
      var picked = L.filterUpsells([].concat.apply([], lists), {
        cartProductIds: ids, addonHandles: addonHandles(), limit: 6
      });
      $('[data-uqc-upsell-list]').innerHTML = picked.map(function (p) { return p.html; }).join('');
      wrap.hidden = picked.length === 0;
    });
  }

  function loadBest() {
    if (bestLoaded) return;
    bestLoaded = true;
    var url = (root.dataset.bestUrl || '/collections/all') + '?sort_by=best-selling&section_id=' + CARDS_SECTION;
    fetchCards(url).then(function (cards) {
      var picked = L.filterUpsells(cards, { cartProductIds: [], addonHandles: addonHandles(), limit: 6 });
      $('[data-uqc-best-list]').innerHTML = picked.map(function (p) { return p.html; }).join('');
      $('[data-uqc-best]').hidden = picked.length === 0;
    });
  }

  /* ---------------- open / close ---------------- */

  function visible(el) {
    return !!(el && el.isConnected && (el.offsetWidth || el.offsetHeight || el.getClientRects().length));
  }

  function setBackgroundInert(on) {
    if (on) {
      var mine = root.closest('.shopify-section') || root;
      Array.prototype.forEach.call(document.body.children, function (el) {
        if (el === mine || el.contains(mine) || el.inert) return;
        if (/^(SCRIPT|STYLE|LINK|DIALOG|TEMPLATE)$/.test(el.tagName) || el.id === 'uforiq-age-gate') return;
        el.inert = true;
        inerted.push(el);
      });
    } else {
      inerted.forEach(function (el) { el.inert = false; });
      inerted = [];
    }
  }

  function setPills(expanded) {
    document.querySelectorAll('.uq-cart-btn').forEach(function (b) {
      b.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    });
  }

  function open(opts) {
    opts = opts || {};
    if (isOpen) {
      if (opts.focus !== false) panel.focus();
      return;
    }
    isOpen = true;
    clearTimeout(closeTimer);
    // Load AgeChecker's popup now so it is bound to the checkout link before the shopper reaches it.
    if (window.UqAgeChecker && typeof window.UqAgeChecker.load === 'function') window.UqAgeChecker.load();
    opener = opts.opener || document.activeElement;
    root.hidden = false;
    void root.offsetWidth;
    root.classList.add('is-open');
    html.classList.add('uqc-lock');
    $('[data-uqc-body]').scrollTop = 0;
    setBackgroundInert(true);
    setPills(true);
    if (opts.focus !== false) panel.focus();
    if (Date.now() - lastRefresh > 15000) refresh();
    loadAddon();
    render();
    if (data.realCount > 0) {
      syncAttributes();
      freshStock(realLines().map(function (l) { return l.variantId; }));
    }
  }

  function close() {
    if (!isOpen) return;
    isOpen = false;
    root.classList.remove('is-open');
    html.classList.remove('uqc-lock');
    setBackgroundInert(false);
    setPills(false);
    hideToast();
    closeTimer = setTimeout(function () { if (!isOpen) root.hidden = true; }, reduceMotion ? 0 : 320);
    var target = opener;
    if (!visible(target) && window.lastFocusedElm && visible(window.lastFocusedElm)) target = window.lastFocusedElm;
    if (!visible(target)) target = document.querySelector('.uq-cart-btn');
    opener = null;
    if (target && typeof target.focus === 'function') target.focus();
  }

  function focusables() {
    return $$('a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])', panel)
      .filter(function (el) { return !el.closest('[hidden]') && visible(el); });
  }

  function onKeydown(e) {
    if (!isOpen) return;
    if (document.querySelector('dialog[open]') || document.getElementById('uforiq-age-gate')) return;
    if (e.key === 'Escape') { e.preventDefault(); close(); return; }
    if (e.key !== 'Tab') return;
    var f = focusables();
    if (!f.length) { e.preventDefault(); panel.focus(); return; }
    var first = f[0], last = f[f.length - 1];
    if (e.shiftKey && (document.activeElement === first || document.activeElement === panel)) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  }

  function onFocusIn(e) {
    if (!isOpen || panel.contains(e.target)) return;
    if (e.target.closest && e.target.closest('dialog[open], #uforiq-age-gate')) return;
    panel.focus();
  }

  /* ---------------- cart actions ---------------- */

  function syncAttributes() {
    return enqueue(function () {
      if (!(data.realCount > 0)) return null;
      var upd = L.attributeUpdate(data.attributes, getArea(), new Date());
      if (!upd) return null;
      return post('/update.js', { attributes: upd.attributes }).then(function (res) {
        if (upd.staleCleared) { staleNote = true; session(STALE_FLAG, '1'); }
        return applySections(res.sections).then(function () {
          if (upd.staleCleared) announce('Your saved delivery time has passed. Pick a new delivery time at checkout.');
        });
      });
    }).catch(function () { /* the cart still works; retry on the next open */ });
  }

  function changeLine(key, qty) {
    var line = lineByKey(key);
    var title = line ? line.title : 'item';
    return enqueue(function () {
      return post('/change.js', { id: key, quantity: qty }).then(function (res) {
        return applySections(res.sections).then(function () {
          var after = (res.items || []).filter(function (i) { return i.key === key; })[0];
          if (qty > 0 && after && after.quantity < qty) {
            lineError(key, 'Only ' + after.quantity + ' of ' + title + ' can be added. Quantity set to ' + after.quantity + '.');
          } else if (qty > 0) {
            announce('Quantity of ' + title + ' updated to ' + qty + '. Subtotal ' + money(data.totalPrice) + '.');
          }
          return res;
        });
      });
    }).catch(function (err) {
      var li = root.querySelector('[data-uqc-line][data-key="' + cssEscape(key) + '"]');
      if (li) { var input = $('[data-uqc-qty-input]', li); if (input) input.value = li.getAttribute('data-qty'); }
      lineError(key, friendlyError(err));
    });
  }

  function cssEscape(s) { return window.CSS && CSS.escape ? CSS.escape(s) : String(s).replace(/"/g, '\\"'); }

  function lineError(key, msg) {
    var li = root.querySelector('[data-uqc-line][data-key="' + cssEscape(key) + '"]');
    var box = li && $('[data-uqc-line-error]', li);
    if (box) { box.textContent = msg; box.hidden = false; }
    alertMsg(msg);
  }

  function scheduleQty(li, qty) {
    var key = li.getAttribute('data-key');
    var input = $('[data-uqc-qty-input]', li);
    if (input) input.value = qty;
    clearTimeout(pendingQty[key]);
    pendingQty[key] = setTimeout(function () {
      delete pendingQty[key];
      changeLine(key, qty);
    }, 350);
  }

  function removeLine(li) {
    var key = li.getAttribute('data-key');
    var line = lineByKey(key);
    if (!line) return;
    clearTimeout(pendingQty[key]);
    enqueue(function () {
      return post('/change.js', { id: key, quantity: 0 }).then(function (res) {
        var rest = (res.items || []);
        var isExtra = function (i) { return extraLines().some(function (a) { return a.variantId === i.variant_id; }); };
        var realLeft = rest.some(function (i) { return !isExtra(i); });
        var addonKeys = rest.filter(isExtra).map(function (i) { return i.key; });
        // A cart holding only Priority Delivery or the small order fee makes no sense: drop them with the last item.
        if (!realLeft && addonKeys.length) {
          return addonKeys.reduce(function (p, k) {
            return p.then(function () { return post('/change.js', { id: k, quantity: 0 }); });
          }, Promise.resolve()).then(function (r2) { return r2; });
        }
        return res;
      }).then(function (res) {
        return applySections(res.sections).then(function () { showUndo(line); });
      });
    }).catch(function (err) { lineError(key, friendlyError(err)); });
  }

  function showUndo(line) {
    var toast = $('[data-uqc-toast]');
    hideToast();
    undoState = { line: line, remaining: UNDO_MS, started: Date.now(), timer: null };
    $('[data-uqc-toast-text]').textContent = 'Removed ' + line.title + '.';
    toast.hidden = false;
    announce('Removed ' + line.title + '. Undo is available for 5 seconds.');
    startUndoTimer();
    var undo = $('[data-uqc-undo]');
    if (isOpen && undo) undo.focus();
  }

  function startUndoTimer() {
    if (!undoState) return;
    clearTimeout(undoState.timer);
    undoState.started = Date.now();
    undoState.timer = setTimeout(function () {
      var hadFocus = root.querySelector('[data-uqc-toast]').contains(document.activeElement);
      hideToast();
      if (hadFocus && isOpen) panel.focus();
    }, undoState.remaining);
  }

  function pauseUndoTimer() {
    if (!undoState) return;
    clearTimeout(undoState.timer);
    undoState.remaining = Math.max(1500, undoState.remaining - (Date.now() - undoState.started));
  }

  function hideToast() {
    if (undoState) clearTimeout(undoState.timer);
    undoState = null;
    var toast = root && root.querySelector('[data-uqc-toast]');
    if (toast) toast.hidden = true;
  }

  function undoRemove() {
    if (!undoState) return;
    var line = undoState.line;
    hideToast();
    var item = { id: line.variantId, quantity: line.qty };
    if (line.properties && Object.keys(line.properties).length) item.properties = line.properties;
    if (line.sellingPlan) item.selling_plan = line.sellingPlan;
    var chk = stockCheck(line.variantId, line.qty);
    if (chk && chk.allowed < 1) { alertMsg(chk.message); return; }
    if (chk && chk.capped) item.quantity = chk.allowed;
    enqueue(function () {
      return post('/add.js', { items: [item] }).then(function (res) {
        return applySections(res.sections).then(function () {
          announce('Restored ' + line.title + '.' + (chk && chk.capped ? ' ' + chk.message : ''));
          var key = res.items && res.items[0] && res.items[0].key;
          var li = key && root.querySelector('[data-uqc-line][data-key="' + cssEscape(key) + '"]');
          ((li && $('.uqc-line__title', li)) || panel).focus();
        });
      });
    }).catch(function (err) { alertMsg(friendlyError(err)); });
  }

  function toggleAddon() {
    var on = data.addonQty > 0;
    if (!on && !addonAllowed()) return;
    enqueue(function () {
      if (on) {
        return addonLines().reduce(function (p, l) {
          return p.then(function () { return post('/change.js', { id: l.key, quantity: 0 }); });
        }, Promise.resolve()).then(function (res) {
          return applySections(res && res.sections).then(function () { announce('Priority Delivery removed.'); });
        });
      }
      if (!addonInfo) return null;
      var chk = stockCheck(addonInfo.variantId, 1);
      if (chk && chk.allowed < 1) { alertMsg(chk.message); return null; }
      return post('/add.js', { items: [{ id: addonInfo.variantId, quantity: 1 }] }).then(function (res) {
        return applySections(res.sections).then(function () { announce('Priority Delivery added.'); });
      });
    }).catch(function (err) { alertMsg(friendlyError(err)); });
  }

  function quickAdd(btn) {
    var card = btn.closest('[data-uqc-card]');
    if (!card) return;
    var title = card.getAttribute('data-title');
    var vid = Number(card.getAttribute('data-variant-id'));
    btn.disabled = true;
    btn.setAttribute('aria-busy', 'true');
    freshStock([vid]).then(function () {
      var chk = stockCheck(vid, 1);
      if (chk && chk.allowed < 1) {
        alertMsg(chk.message);
        if (btn.isConnected) { btn.disabled = false; btn.removeAttribute('aria-busy'); }
        return;
      }
      quickAddSend(btn, card, title, vid);
    });
  }

  function quickAddSend(btn, card, title, vid) {
    enqueue(function () {
      var props = { _uq_upsell: card.getAttribute('data-product-id') };
      return post('/add.js', { items: [{ id: vid, quantity: 1, properties: props }] }).then(function (res) {
        return applySections(res.sections).then(function () {
          announce('Added ' + title + ' to your cart.');
          var key = res.items && res.items[0] && res.items[0].key;
          var li = key && root.querySelector('[data-uqc-line][data-key="' + cssEscape(key) + '"]');
          if (li) $('.uqc-line__title', li).focus();
        });
      });
    }).catch(function (err) {
      alertMsg(friendlyError(err));
    }).then(function () {
      if (btn.isConnected) { btn.disabled = false; btn.removeAttribute('aria-busy'); }
    });
  }

  function discountMsg(text, ok) {
    var el = $('[data-uqc-discount-msg]');
    el.textContent = text;
    el.hidden = !text;
    el.classList.toggle('is-error', !ok);
    el.classList.toggle('is-ok', !!ok);
    if (text) (ok ? announce : alertMsg)(text);
  }

  function applyDiscount(code) {
    code = (code || '').trim();
    if (!code) { discountMsg('Enter a discount code.', false); return; }
    var existing = data.discountCodes.slice();
    var lower = existing.map(function (c) { return c.toLowerCase(); });
    if (lower.indexOf(code.toLowerCase()) !== -1) { discountMsg('Code ' + code + ' is already applied.', true); return; }
    var btn = $('.uqc-discount__apply');
    btn.disabled = true;
    enqueue(function () {
      return post('/update.js', { discount: existing.concat(code).join(',') }).then(function (res) {
        if (!Array.isArray(res.discount_codes)) {
          // This store's Cart API doesn't take codes: use the /discount/ route and come back here.
          var back = window.location.pathname + window.location.search;
          back += (back.indexOf('?') === -1 ? '?' : '&') + 'uq_cart=open';
          window.location.href = rootUrl() + 'discount/' + encodeURIComponent(code) + '?redirect=' + encodeURIComponent(back);
          return null;
        }
        var hit = res.discount_codes.filter(function (c) { return String(c.code).toLowerCase() === code.toLowerCase(); })[0];
        if (hit && hit.applicable) {
          $('#uqc-discount-input').value = '';
          return applySections(res.sections).then(function () { discountMsg('Code ' + hit.code + ' applied.', true); });
        }
        // Not valid for this cart: take it back off so it doesn't sit on the cart unseen.
        return post('/update.js', { discount: existing.join(',') }).then(function (r2) {
          return applySections(r2.sections).then(function () {
            discountMsg('Code ' + code + " can't be used with this cart.", false);
          });
        });
      });
    }).catch(function (err) {
      discountMsg(friendlyError(err), false);
    }).then(function () { btn.disabled = false; });
  }

  function removeDiscount(code) {
    var rest = data.discountCodes.filter(function (c) { return c.toLowerCase() !== String(code).toLowerCase(); });
    enqueue(function () {
      return post('/update.js', { discount: rest.join(',') }).then(function (res) {
        return applySections(res.sections).then(function () {
          discountMsg('Code ' + code + ' removed.', true);
          $('#uqc-discount-input').focus();
        });
      });
    }).catch(function (err) { discountMsg(friendlyError(err), false); });
  }

  function goCheckout(href) {
    var done = false;
    function go() {
      if (done) return;
      done = true;
      // Backstop: never send a cart that is over the fulfilling location's stock to checkout.
      if (stockBlocked()) { stockAlertAndFocus(); return; }
      staleNote = false; session(STALE_FLAG, null); window.location.href = href;
    }
    setTimeout(go, 2500);
    // Wait for the drawer's attribute sync, the scheduling widget's last write and one live stock re-read
    // (capped at 2.5 s, fails open), so the order carries both and stays within stock.
    var pending = [syncAttributes()];
    var St = window.UqStock;
    if (St) pending.push(St.refresh(realLines().map(function (l) { return l.variantId; }), { force: true }));
    var sched = window.UforiqSchedule;
    if (sched && typeof sched.flush === 'function') pending.push(sched.flush());
    Promise.all(pending).then(go, go);
  }

  /* Add-to-cart from any product form (product page, sticky bar, quick view, collection quick-add). */
  function formError(form, msg) {
    var errId = 'uqc-form-error-' + (form.id || 'form');
    var oldErr = document.getElementById(errId);
    if (oldErr) oldErr.remove();
    if (!msg) return;
    var el = document.createElement('p');
    el.id = errId;
    el.className = 'alert alert--error uqc-form-error';
    el.setAttribute('role', 'alert');
    el.textContent = msg;
    (form.parentElement || form).appendChild(el);
  }

  function addFromForm(form, btn) {
    var fd = new FormData(form);
    fd.append('sections', SECTION);
    fd.append('sections_url', window.location.pathname);
    if (btn) { btn.classList.add('working'); btn.setAttribute('aria-busy', 'true'); }
    formError(form, '');
    var vid = fd.get('id');
    var want = parseInt(fd.get('quantity'), 10) || 1;
    freshStock([vid]).then(function () {
      var chk = stockCheck(vid, want);
      if (chk && chk.allowed < 1) {
        formError(form, chk.message);
        if (btn) { btn.classList.remove('working'); btn.removeAttribute('aria-busy'); }
        if (window.UqStock) window.UqStock.applyForms();
        return;
      }
      if (chk && chk.capped) fd.set('quantity', String(chk.allowed));
      addFromFormSend(form, btn, fd, chk && chk.capped ? chk.message : '');
    });
  }

  function addFromFormSend(form, btn, fd, capMsg) {
    enqueue(function () {
      return nativeFetch(cartUrl('/add.js'), {
        method: 'POST',
        credentials: 'same-origin',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: fd
      }).then(parseJson).then(function (res) {
        var title = res.product_title || (res.items && res.items[0] && res.items[0].product_title) || 'Item';
        return applySections(res.sections).then(function () {
          var modal = form.closest('modal-box');
          if (modal && typeof modal.hide === 'function') { try { modal.hide(); } catch (e) { /* ignore */ } }
          // Product cards swap to a -/+ stepper (syncCards) instead of opening the drawer.
          var card = form.closest('[data-uq-card]');
          if (!card) open({ opener: btn || form });
          else { var s = $('[data-uq-card-step="1"]', card); if (s && btn === document.activeElement) s.focus(); }
          if (capMsg) { alertMsg(capMsg + ' We added what was left to your cart.'); if (card) cardMsg(card, capMsg); }
          else announce('Added ' + title + ' to your cart.');
        });
      });
    }).catch(function (err) {
      formError(form, friendlyError(err));
    }).then(function () {
      if (btn) { btn.classList.remove('working'); btn.removeAttribute('aria-busy'); }
    });
  }

  /* ---------------- product card steppers ----------------
   * snippets/quick-buy.liquid (card_stepper) marks single-variant product cards with data-uq-card.
   * Once the variant is in the cart the Add to cart button gives way to a -/+ stepper showing the
   * cart quantity. Steps are debounced like the drawer stepper and go through changeLine (queue,
   * stock limits); the drawer stays closed. */
  var cardTarget = {};
  var cardTimer = {};

  function variantQty(vid) {
    return realLines().reduce(function (n, l) { return String(l.variantId) === String(vid) ? n + l.qty : n; }, 0);
  }
  function firstLineFor(vid) {
    return realLines().filter(function (l) { return String(l.variantId) === String(vid); })[0] || null;
  }
  function cardMsg(card, msg) {
    var box = $('[data-uq-card-msg]', card);
    if (!box) return;
    box.textContent = msg || '';
    box.hidden = !msg;
  }
  function syncCards() {
    $$('[data-uq-card]', document).forEach(function (card) {
      var vid = card.getAttribute('data-variant-id');
      if (cardTimer[vid]) return; // a step is pending; keep the optimistic count
      var qty = variantQty(vid);
      var box = $('[data-uq-card-qty]', card);
      var count = $('[data-uq-card-count]', card);
      if (count) count.textContent = qty;
      if (box) { box.hidden = qty < 1; box.classList.remove('is-busy'); }
      if (qty > 0) card.setAttribute('data-in-cart', ''); else { card.removeAttribute('data-in-cart'); cardMsg(card, ''); }
    });
  }
  function cardStep(btn) {
    var card = btn.closest('[data-uq-card]');
    var vid = card.getAttribute('data-variant-id');
    var line = firstLineFor(vid);
    if (!line) { syncCards(); return; }
    var dir = Number(btn.getAttribute('data-uq-card-step'));
    var shown = cardTarget[vid] != null ? cardTarget[vid] : variantQty(vid);
    var others = variantQty(vid) - line.qty; // same variant on another line (rare)
    var next = Math.max(others, shown + dir);
    cardMsg(card, '');
    if (dir > 0) {
      var v = verdictFor(line.key);
      if (v && next - others > v.maxForLine) { cardMsg(card, v.message); alertMsg(v.message); return; }
    }
    cardTarget[vid] = next;
    $('[data-uq-card-count]', card).textContent = next;
    $('[data-uq-card-qty]', card).classList.add('is-busy');
    clearTimeout(cardTimer[vid]);
    cardTimer[vid] = setTimeout(function () {
      var want = cardTarget[vid] - others;
      changeLine(line.key, want).then(function () {
        delete cardTimer[vid];
        delete cardTarget[vid];
        var got = variantQty(vid) - others;
        if (want > 0 && got < want) cardMsg(card, 'Only ' + got + ' available.');
        syncCards();
      });
    }, 350);
  }

  /* Size buttons on one-option product cards (snippets/quick-buy.liquid size_chips): switch the card to
   * that variant (cart variant, stepper count, price, availability, link). */
  function cardSize(btn) {
    var card = btn.closest('[data-uq-card]');
    var vid = btn.getAttribute('data-variant-id');
    if (!card || card.getAttribute('data-variant-id') === vid) return;
    $$('[data-uq-card-size]', card).forEach(function (b) { b.setAttribute('aria-pressed', b === btn ? 'true' : 'false'); });
    card.setAttribute('data-variant-id', vid);
    var input = $('input[name="id"]', card);
    if (input) input.value = vid;
    var avail = btn.getAttribute('data-available') === 'true';
    var add = $('[data-js-product-add-to-cart]', card);
    if (add) {
      add.disabled = !avail;
      var txt = $('[data-uq-add-text]', add);
      if (txt) txt.textContent = avail ? txt.getAttribute('data-uq-add-text') : txt.getAttribute('data-uq-sold-text');
    }
    cardMsg(card, '');
    syncCards();
    // Re-run the stock limit for the new size (else the old size's "Only N available" block stays).
    if (window.UqStock) window.UqStock.applyForms();
    var item = card.closest('.product-item') || card.parentElement;
    var price = $('[data-uq-card-price]', item);
    var tpl = $('template[data-uq-price-for="' + vid + '"]', card);
    if (price && tpl) price.innerHTML = tpl.innerHTML;
    var pa = $('pickup-availability-compact', item);
    if (pa) {
      pa.dataset.variantId = vid;
      if (typeof pa.fetchAvailability === 'function') pa.fetchAvailability(vid, true);
    }
    $$('a[href*="/products/"]', item).forEach(function (a) {
      try {
        var u = new URL(a.href, location.href);
        if (!u.searchParams.has('variant')) return;
        u.searchParams.set('variant', vid);
        a.setAttribute('href', u.pathname + u.search + u.hash);
      } catch (err) {}
    });
  }

  /* Called from the capture listeners in snippets/uq-cart-head.liquid. */
  function onClick(e) {
    if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    var t = e.target && e.target.closest ? e.target : e.target && e.target.parentElement;
    if (!t) return;
    var pill = t.closest('.uq-cart-btn, [data-uq-cart-open]');
    if (pill && !root.contains(pill)) {
      e.preventDefault();
      e.stopImmediatePropagation();
      open({ opener: pill });
      return;
    }
    var co = t.closest('[data-uqc-checkout]');
    if (co && root.contains(co)) {
      e.preventDefault();
      e.stopImmediatePropagation();
      if (stockBlocked()) { stockAlertAndFocus(); return; }
      // This capture listener runs before the scheduling widget's own checkout gate (and stops it), so ask the
      // widget here: until delivery or shipping is fully chosen it shows why and moves focus to the widget.
      var sched = window.UforiqSchedule;
      if (sched && typeof sched.isComplete === 'function' && !sched.isComplete()) {
        if (typeof sched.guardCheckout === 'function') sched.guardCheckout();
        return;
      }
      // AgeChecker (snippets/uforiq-agechecker-scope.liquid): its popup must see this click before checkout.
      // gate() returns true when it owns the click and calls the callback only if checkout may go ahead.
      var href = co.getAttribute('href');
      var ac = window.UqAgeChecker;
      if (ac && typeof ac.gate === 'function' &&
          ac.gate(co, function () { goCheckout(href); }, function (msg) { window.alert(msg); })) return;
      goCheckout(href);
    }
  }

  function onSubmit(e) {
    var form = e.target;
    if (!form || form.tagName !== 'FORM' || root.contains(form) || form.hasAttribute('data-uq-cart-skip')) return;
    var action = form.getAttribute('action') || '';
    if (!/\/cart\/add(\.js)?\/?(\?|#|$)/.test(action) || !form.querySelector('[name="id"]')) return;
    // Krown's required custom line-item fields: let the theme validate those itself.
    var page = form.closest('[data-js-product-component]');
    if (page && $$('[data-js-custom-property] [required]', page).some(function (f) { return !f.value || (f.type === 'checkbox' && !f.checked); })) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    var btn = e.submitter || form.querySelector('[type="submit"]');
    if (btn && (btn.disabled || btn.classList.contains('disabled') || btn.getAttribute('aria-disabled') === 'true')) return;
    addFromForm(form, btn);
  }

  /* ---------------- events ---------------- */

  function bind() {
    document.addEventListener('click', function (e) {
      var sz = e.target.closest && e.target.closest('[data-uq-card-size]');
      if (sz) { e.preventDefault(); e.stopPropagation(); cardSize(sz); return; }
      var cs = e.target.closest && e.target.closest('[data-uq-card-step]');
      if (!cs) return;
      e.preventDefault();
      e.stopPropagation();
      cardStep(cs);
    });
    document.addEventListener('click', function (e) {
      if (!root.contains(e.target)) return;
      var t = e.target;
      if (t.closest('[data-uqc-close]')) { close(); return; }
      var step = t.closest('[data-uqc-step]');
      if (step) {
        var li = step.closest('[data-uqc-line]');
        var input = $('[data-uqc-qty-input]', li);
        var next = Math.max(0, (parseInt(input.value, 10) || 0) + Number(step.getAttribute('data-uqc-step')));
        if (Number(step.getAttribute('data-uqc-step')) > 0) {
          var v = verdictFor(li.getAttribute('data-key'));
          if (v && next > v.maxForLine) { lineError(li.getAttribute('data-key'), v.message); return; }
        }
        if (next === 0) removeLine(li); else scheduleQty(li, next);
        return;
      }
      var red = t.closest('[data-uqc-reduce]');
      if (red) {
        var rli = red.closest('[data-uqc-line]');
        var n = parseInt(red.getAttribute('data-uqc-reduce'), 10);
        if (n > 0) changeLine(rli.getAttribute('data-key'), n); else removeLine(rli);
        return;
      }
      var rm = t.closest('[data-uqc-remove]');
      if (rm) { removeLine(rm.closest('[data-uqc-line]')); return; }
      if (t.closest('[data-uqc-undo]')) { undoRemove(); return; }
      if (t.closest('[data-uqc-addon-toggle]')) { toggleAddon(); return; }
      var qa = t.closest('[data-uqc-quick-add]');
      if (qa) { quickAdd(qa); return; }
      var codeBtn = t.closest('[data-uqc-code-remove]');
      if (codeBtn) { removeDiscount(codeBtn.getAttribute('data-uqc-code-remove')); return; }
      if (t.closest('[data-uqc-choose-area]') && window.UforiqLocation) { window.UforiqLocation.open(); }
    });

    document.addEventListener('change', function (e) {
      if (!root.contains(e.target) || !e.target.matches('[data-uqc-qty-input]')) return;
      var li = e.target.closest('[data-uqc-line]');
      var n = parseInt(e.target.value, 10);
      if (isNaN(n) || n < 0) { e.target.value = li.getAttribute('data-qty'); return; }
      if (n === 0) { removeLine(li); return; }
      var v = verdictFor(li.getAttribute('data-key'));
      if (v && n > v.maxForLine) {
        lineError(li.getAttribute('data-key'), v.message);
        if (v.maxForLine < 1 || v.maxForLine === Number(li.getAttribute('data-qty'))) { e.target.value = li.getAttribute('data-qty'); return; }
        n = v.maxForLine;
      }
      if (String(n) !== li.getAttribute('data-qty')) scheduleQty(li, n);
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' && e.target.matches && e.target.matches('[data-uqc-qty-input]') && root.contains(e.target)) {
        e.preventDefault();
        e.target.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });

    document.addEventListener('submit', function (e) {
      if (!e.target.matches('[data-uqc-discount]') || !root.contains(e.target)) return;
      e.preventDefault();
      applyDiscount($('#uqc-discount-input').value);
    });

    var toast = function () { return root.querySelector('[data-uqc-toast]'); };
    document.addEventListener('focusin', function (e) { if (toast() && toast().contains(e.target)) pauseUndoTimer(); });
    document.addEventListener('focusout', function (e) {
      if (toast() && toast().contains(e.target) && !toast().contains(e.relatedTarget)) startUndoTimer();
    });
    document.addEventListener('mouseover', function (e) { if (toast() && toast().contains(e.target)) pauseUndoTimer(); });
    document.addEventListener('mouseout', function (e) {
      if (toast() && toast().contains(e.target) && !toast().contains(e.relatedTarget) && !toast().contains(document.activeElement)) startUndoTimer();
    });

    document.addEventListener('keydown', onKeydown);
    document.addEventListener('focusin', onFocusIn);

    // Phones: while a text field in the cart body (the ZIP box) has focus, hide the sticky footer so
    // the on-screen keyboard leaves room to see the field instead of the checkout button stack.
    var TYPING_FIELD = 'input:not([type=checkbox]):not([type=radio]):not([type=button]):not([type=submit]), textarea, select';
    var typingMq = window.matchMedia('(max-width: 600px)');
    document.addEventListener('focusin', function (e) {
      var body = root.querySelector('.uqc__body');
      if (typingMq.matches && body && body.contains(e.target) && e.target.matches(TYPING_FIELD)) panel.classList.add('is-typing');
    });
    document.addEventListener('focusout', function (e) {
      var body = root.querySelector('.uqc__body');
      if (body && body.contains(e.relatedTarget) && e.relatedTarget.matches(TYPING_FIELD)) return;
      panel.classList.remove('is-typing');
    });

    // The scheduling widget adds and removes the Priority Delivery and small order fee lines itself: re-render to match.
    window.addEventListener('uforiq:schedule:cart-changed', function (e) {
      var reason = e && e.detail && e.detail.reason;
      refresh().then(function () {
        if (!isOpen) return;
        if (reason === 'fee-added') announce('Small order fee added for orders under ' + money(widgetConfig().feeThreshold || L.SMALL_ORDER_FEE.thresholdCents) + '.');
        else if (reason === 'fee-removed') announce('Small order fee removed.');
      });
    });

    // The widget switched delivery and shipping (or confirmed a zone): the banner follows the method.
    window.addEventListener('uforiq:schedule:change', function (e) {
      lastSched = (e && e.detail) || null;
      renderRewards();
    });

    // Area, method, cart or live stock numbers changed: re-check every line.
    window.addEventListener('uq:stock:change', function () { renderStock(); });

    window.addEventListener('uforiq:location:changed', function () {
      renderRewards();
      renderAddon();
      if (data.realCount > 0) syncAttributes();
      if (isOpen) {
        setTimeout(function () {
          if (isOpen && !panel.contains(document.activeElement) && !document.querySelector('dialog[open]')) {
            var b = $('[data-uqc-choose-area]');
            (b || panel).focus();
          }
        }, 80);
      }
    });

    window.addEventListener('pageshow', function (e) { if (e.persisted) refresh(); });
    document.addEventListener('visibilitychange', function () {
      if (document.visibilityState === 'visible' && Date.now() - lastRefresh > 15000) refresh();
    });

    // Theme editor: show the drawer while its section is selected.
    document.addEventListener('shopify:section:select', function (e) { if (e.detail && e.detail.sectionId === SECTION) open({ focus: false }); });
    document.addEventListener('shopify:section:deselect', function (e) { if (e.detail && e.detail.sectionId === SECTION) close(); });
    document.addEventListener('shopify:section:load', function (e) {
      if (e.detail && e.detail.sectionId === SECTION) { setup(); }
    });
  }

  /* Other scripts (the /cart page, an app still running, Krown) can change the cart too. Watch their
   * cart requests and refresh the drawer and the header pill afterwards (audit #6). Requests from
   * this file use the saved nativeFetch and are not seen here. */
  var CART_WRITE = /\/cart\/(add|change|update|clear)(\.js)?\/?(\?|$)/;
  var externalTimer = null;
  function externalChange(body) {
    if (typeof body === 'string' && /OpusCartId/.test(body) && !/"(items|updates|id|quantity)"/.test(body)) return;
    clearTimeout(externalTimer);
    externalTimer = setTimeout(refresh, 300);
  }
  function watchExternalCartWrites() {
    window.fetch = function (input, init) {
      var p = nativeFetch.apply(window, arguments);
      try {
        var url = typeof input === 'string' ? input : (input && input.url) || '';
        var method = ((init && init.method) || (input && input.method) || 'GET').toUpperCase();
        if (method === 'POST' && CART_WRITE.test(url)) {
          var body = init && init.body;
          p.then(function () { externalChange(body); }, function () {});
        }
      } catch (e) { /* never break the caller */ }
      return p;
    };
    var XHR = window.XMLHttpRequest && window.XMLHttpRequest.prototype;
    if (!XHR) return;
    var origOpen = XHR.open, origSend = XHR.send;
    XHR.open = function (method, url) {
      this.__uqcWatch = String(method).toUpperCase() === 'POST' && CART_WRITE.test(String(url));
      return origOpen.apply(this, arguments);
    };
    XHR.send = function (body) {
      if (this.__uqcWatch) this.addEventListener('loadend', function () { externalChange(body); });
      return origSend.apply(this, arguments);
    };
  }

  function detectEgo() {
    var found = document.querySelector('#opus-shadow-container, #ego-shadow-container, script[src*="opus-cart"], script[src*="ego-cart"], script[src*="opusecom"], link[href*="opus-cart"], link[href*="ego-cart"]');
    if (found && !html.hasAttribute('data-uq-cart-ego')) {
      html.setAttribute('data-uq-cart-ego', '');
      if (window.console && console.info) console.info('[UFORIQ cart] Ego (formerly Opus) Cart Upsell is still enabled on this theme. The UFORIQ drawer is handling the cart; turn the app embed off before publishing (docs/CART.md).');
    }
  }

  function setup() {
    root = document.querySelector('[data-uq-cart]');
    panel = root.querySelector('[data-uqc-panel]');
    readData();
    render();
  }

  function init() {
    setup();
    bind();
    watchExternalCartWrites();
    document.querySelectorAll('.uq-cart-btn').forEach(function (b) {
      b.setAttribute('aria-haspopup', 'dialog');
      b.setAttribute('aria-controls', 'uq-cart');
      b.setAttribute('aria-expanded', 'false');
    });
    detectEgo();
    setTimeout(detectEgo, 3000);
    if (data.realCount > 0) { loadAddon(); syncAttributes(); }
    window.UqCart = { ready: true, onClick: onClick, onSubmit: onSubmit, open: open, close: close, refresh: refresh };
    // Coming back from the /discount/ fallback.
    if (/[?&]uq_cart=open\b/.test(window.location.search)) {
      try {
        var u = new URL(window.location.href);
        u.searchParams.delete('uq_cart');
        history.replaceState(history.state, '', u.pathname + u.search + u.hash);
      } catch (e) { /* ignore */ }
      open();
    }
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
